import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICalculatorState, ISubmittedApplication } from '../../types';
import { IOfferForAnalytic } from '../../utils/tracking/types';

interface ISetRedirectUrlPayloadAction {
  url: string;
}

interface IOpenAuthorizationModalPayloadAction {
  redirectUrl?: string;
}

interface ISetAuthorizationModalRedirectUrlPayloadAction {
  redirectUrl: string | null;
}

interface IOpenUnfinishedAnketaModalPayloadAction {
  offerForAnalytic: IOfferForAnalytic | null;
  /* Обе кнопки модалки ведут по этой ссылке, поэтому без неё модалку не открывают вовсе */
  newAnketaUrl: string;
}

interface IOpenSubmittedAnketaModalPayloadAction {
  offerForAnalytic: IOfferForAnalytic | null;
  application: ISubmittedApplication;
}

// Новый интерфейс для сохранения результата saveOffer
interface ISaveOfferResultPayloadAction {
  applicationId: number;
  redirectUrl: string;
}

export const initialState: ICalculatorState = {
  authorizationModal: {
    isOpen: false,
    redirectUrl: null,
  },
  unfinishedAnketaModal: {
    isOpen: false,
    offerForAnalytic: null,
    newAnketaUrl: null,
  },
  submittedAnketaModal: {
    isOpen: false,
    offerForAnalytic: null,
    application: null,
  },
  user: {
    isAuthorized: false,
    isCianAuthorized: false,
  },
  saveOfferResult: null,
  isErrorGetOffersListing: false,
  isOffersFetching: false,
  isCianAuthorizedModalOpen: false,
  entityId: 0,
  initialFormState: {
    objectCost: 1000000,
    loanTermYears: 30,
    downPayment: 5010000,
    regionId: 2,
    realEstateType: 'newBuilding',
    benefitProgram: undefined,
    objectType: 'flat',
    newbuildingSeoLinkUrl: undefined,
  },
  defaultRedirectUrl: '',
  financePlatformEnabled: false,
  skipOffersFetch: true,
  /* Приезжает только с v3-выдачей (getOffers meta): до её ответа сохранять выдачу нечем,
     поэтому пустая строка, а не выдуманный идентификатор — на нём стоят гарды обеих групп */
  programsVersion: '',
  shortFormRedirectUrl: null,
  shortFormGroup: null,
};

export const calculatorSlice = createSlice({
  initialState,
  name: 'calculator',
  reducers: {
    openAuthorizationModal: (state, action: PayloadAction<IOpenAuthorizationModalPayloadAction | undefined>) => {
      state.authorizationModal.isOpen = true;
      state.authorizationModal.redirectUrl = action.payload?.redirectUrl || null;
    },
    closeAuthorizationModal: state => {
      state.authorizationModal.isOpen = false;
      state.authorizationModal.redirectUrl = null;
      state.saveOfferResult = null;
    },
    openUnfinishedAnketaModal: (state, action: PayloadAction<IOpenUnfinishedAnketaModalPayloadAction>) => {
      state.unfinishedAnketaModal.isOpen = true;
      state.unfinishedAnketaModal.offerForAnalytic = action.payload.offerForAnalytic;
      state.unfinishedAnketaModal.newAnketaUrl = action.payload.newAnketaUrl;
    },
    closeUnfinishedAnketaModal: state => {
      state.unfinishedAnketaModal.isOpen = false;
      state.unfinishedAnketaModal.offerForAnalytic = null;
      state.unfinishedAnketaModal.newAnketaUrl = null;
    },
    openSubmittedAnketaModal: (state, action: PayloadAction<IOpenSubmittedAnketaModalPayloadAction>) => {
      state.submittedAnketaModal.isOpen = true;
      state.submittedAnketaModal.offerForAnalytic = action.payload.offerForAnalytic;
      state.submittedAnketaModal.application = action.payload.application;
    },
    closeSubmittedAnketaModal: state => {
      state.submittedAnketaModal.isOpen = false;
      state.submittedAnketaModal.offerForAnalytic = null;
      state.submittedAnketaModal.application = null;
    },
    setIsOffersFetching: (state, action: PayloadAction<boolean>) => {
      state.isOffersFetching = action.payload;
    },
    setSaveOfferResult: (state, action: PayloadAction<ISaveOfferResultPayloadAction>) => {
      state.saveOfferResult = {
        applicationId: action.payload.applicationId,
        redirectUrl: action.payload.redirectUrl,
      };
    },
    clearSaveOfferResult: state => {
      state.saveOfferResult = null;
    },
    setIsErrorGetOffersListing: (state, action: PayloadAction<boolean>) => {
      state.isErrorGetOffersListing = action.payload;
    },
    setRedirectUrl: (state, action: PayloadAction<ISetRedirectUrlPayloadAction>) => {
      state.defaultRedirectUrl = action.payload.url;
    },
    setFinancePlatformEnabled: (state, action: PayloadAction<boolean>) => {
      state.financePlatformEnabled = action.payload;
    },
    setDefaultFormState: (state, action: PayloadAction<Partial<ICalculatorState['initialFormState']>>) => {
      state.initialFormState = { ...state.initialFormState, ...action.payload };
    },
    setSkipOffersFetch: (state, action: PayloadAction<boolean>) => {
      state.skipOffersFetch = action.payload;
    },
    setProgramsVersion: (state, action: PayloadAction<string>) => {
      state.programsVersion = action.payload;
    },
    setEntityId: (state, action: PayloadAction<number>) => {
      state.entityId = action.payload;
    },
    setCianAuthorizedModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCianAuthorizedModalOpen = action.payload;
    },
    setAuthorizationModalRedirectUrl: (
      state,
      action: PayloadAction<ISetAuthorizationModalRedirectUrlPayloadAction>,
    ) => {
      state.authorizationModal.redirectUrl = action.payload.redirectUrl;
    },
    setIsCianAuthorized: (state, action: PayloadAction<boolean>) => {
      state.user.isCianAuthorized = action.payload;
    },
    setShortFormRedirectUrl: (state, action: PayloadAction<string>) => {
      state.shortFormRedirectUrl = action.payload;
    },
    setShortFormGroup: (state, action: PayloadAction<string>) => {
      state.shortFormGroup = action.payload;
    },
  },
});

export const {
  openAuthorizationModal,
  closeAuthorizationModal,
  openUnfinishedAnketaModal,
  closeUnfinishedAnketaModal,
  openSubmittedAnketaModal,
  closeSubmittedAnketaModal,
  setIsOffersFetching,
  setSaveOfferResult,
  clearSaveOfferResult,
  setIsErrorGetOffersListing,
  setRedirectUrl,
  setFinancePlatformEnabled,
  setDefaultFormState,
  setSkipOffersFetch,
  setProgramsVersion,
  setEntityId,
  setCianAuthorizedModalOpen,
  setAuthorizationModalRedirectUrl,
  setIsCianAuthorized,
  setShortFormRedirectUrl,
  setShortFormGroup,
} = calculatorSlice.actions;
